/**
 * Auth Helpers
 *
 * API response types, type guards, normalization functions, and error handling
 * for the authentication context. Extracted to keep auth-context.tsx focused
 * on the React context/provider logic.
 */

import axios, { AxiosError } from 'axios';
import type { AuthUser, Department } from '@/types';
import type { Schemas } from '@/types/api';
import { fromApi } from '@/types/api.codec';

// Os tipos da resposta vêm do contrato: `DepartmentRead` e `MeResponse`.
type ApiDepartment = Schemas["DepartmentRead"];
type ApiMeResponse = Schemas["MeResponse"];

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for department object
 * Validates required fields from DepartmentRead schema
 */
const isValidDepartment = (dept: unknown): dept is ApiDepartment => {
  if (!dept || typeof dept !== 'object') return false;
  const d = dept as Record<string, unknown>;

  return (
    typeof d.id === 'string' &&
    typeof d.name === 'string' &&
    typeof d.description === 'string' &&
    typeof d.lead === 'string' &&
    typeof d.headcount === 'number' &&
    typeof d.is_system === 'boolean' &&
    typeof d.is_visible === 'boolean' &&
    Array.isArray(d.menu_paths) &&
    typeof d.created_at === 'string'
  );
};

/**
 * Type guard for /auth/me response
 * Validates required fields from MeResponse schema
 */
const isValidMeResponse = (payload: unknown): payload is ApiMeResponse => {
  if (!payload || typeof payload !== 'object') return false;
  const obj = payload as Record<string, unknown>;

  return (
    typeof obj.id === 'string' &&
    typeof obj.name === 'string' &&
    typeof obj.email === 'string' &&
    typeof obj.is_superuser === 'boolean' &&
    typeof obj.last_active === 'string' &&
    Array.isArray(obj.departments) &&
    obj.email.includes('@')
  );
};

// ============================================================================
// NORMALIZATION FUNCTIONS
// ============================================================================

const normalizeDepartment = (dept: ApiDepartment): Department => fromApi("DepartmentRead", dept);

/**
 * Normalize /auth/me response to AuthUser
 * Transforms: ApiMeResponse -> AuthUser
 */
export const normalizeAuthUser = (payload: unknown): AuthUser => {
  // Validate response structure
  if (!isValidMeResponse(payload)) {
    console.error('Invalid /auth/me response structure:', payload);
    throw new Error('Received invalid user data from server. Please contact support.');
  }

  // Validate and normalize departments
  const departments: Department[] = payload.departments
    .filter((dept) => {
      const isValid = isValidDepartment(dept);
      if (!isValid) {
        console.warn('Skipping invalid department:', dept);
      }
      return isValid;
    })
    .map(normalizeDepartment);

  // Return normalized AuthUser
  return {
    id: payload.id,
    email: payload.email,
    name: payload.name,
    isSuperuser: payload.is_superuser,
    isSecurityAccount: payload.is_security_account ?? false,
    lastActive: payload.last_active,
    departments,
    profile_image_url: payload.profile_image_url,
    permissions: payload.permissions || [],
    // Sem isto, o backend enviava-o e esta allowlist deitava-o fora — a barra de
    // aviso nunca aparecia e a sessão parecia normal.
    impersonated_by: payload.impersonated_by ?? null,
  };
};

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Extract meaningful error message from axios error
 */
export const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string; message?: string }>;
    return (
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.message ||
      axiosError.message ||
      fallback
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};
